import { useAsyncState } from "@vueuse/core";
import { computed, onScopeDispose, shallowRef } from "vue";

/**
 * Returns async request state and controls that publish only the latest execution without aborting tasks.
 * With `throwError`, `execute` rejects when the latest execution fails; a superseded one never rejects.
 */
export function useLatestRequest(task, { throwError = false } = {}) {
  const current = shallowRef();
  const state = computed(() => current.value?.state.value);
  const error = computed(() => current.value?.error.value);
  const loading = computed(() => current.value?.isLoading?.value ?? false);
  let disposed = false;

  function cancel() {
    current.value = { state: shallowRef(state.value), error: shallowRef(error.value) };
  }

  function reset() {
    current.value = undefined;
  }

  async function execute(...args) {
    if (disposed) {
      return;
    }

    const request = useAsyncState(async () => task(...args), state.value, {
      immediate: false,
      resetOnExecute: false,
      onError: clearFailedState,
    });

    function clearFailedState() {
      request.state.value = undefined;
    }

    current.value = request;
    await request.execute();

    if (current.value !== request) {
      return undefined;
    }

    if (throwError && request.error.value) {
      throw request.error.value;
    }

    return request.state.value;
  }

  function dispose() {
    disposed = true;
    cancel();
  }

  onScopeDispose(dispose);
  return { state, error, loading, execute, cancel, reset };
}
